import React, { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Toolbar,
  alpha,
  TableSortLabel,
  Popover,
} from "@mui/material";
import {
  FaSearch,
  FaEdit,
  FaHistory,
  FaFilter,
  FaTimes,
  FaCalendarCheck,
  FaBuilding,
  FaStore,
  FaTrash,
  FaClock,
  FaCheck,
  FaEye,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaDownload,
} from "react-icons/fa";
import {
  MdEventAvailable,
  MdSell,
  MdRedeem,
  MdOutlineAssignment,
  MdClear,
  MdNewReleases,
} from "react-icons/md";
import {
  getGiftCertificates,
  getDropdownData,
  updateGCStatus,
  getGCDetails,
  deleteGiftCertificate,
  selectGiftCertificate,
  GiftCertificateState,
  resetGiftCertificate,
  clearCurrentCertificate,
  GiftCertificate,
  StatusHistory,
  getUserStores,
  getUserPermissions,
  getValidNextStatuses,
  requestStatusChange,
  getPendingRequests,
  reviewStatusRequest,
  getAvailableStatuses,
  bulkApproveRequests,
  getGCAttachments,
  UserPermissions,
  StatusChangeRequest,
} from "../slices/gift-certificate.slice";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import StatusTooltip from "./StatusTooltip";

// UPDATED: New status labels with descriptions
const STATUS_LABELS = {
  Created: "Created - NEW",
  Released: "Released - WH",
  "Receive Approval": "Receive Approval - OIC",
  Received: "Received - STR",
  "Sold Approval": "Sold Approval - OIC",
  Sold: "Sold - CSHR",
  "Redeem Approval": "Redeem Approval - OIC",
  Redeemed: "Redeemed - CSHR",
  Expired: "Expired",
};

// ADD: Status order for sorting
const STATUS_ORDER = [
  "Created",
  "Released",
  "Receive Approval",
  "Received",
  "Sold Approval",
  "Sold",
  "Redeem Approval",
  "Redeemed",
  "Expired",
];

interface ColumnOption {
  key: string;
  label: string;
  selected: boolean;
  mandatory: boolean;
}

// ADD: Sorting configuration interface
interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

export function ManageGiftCertificates() {
  const dispatch = useAppDispatch();
  const giftCertificateState = useAppSelector(selectGiftCertificate);

  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const is_admin = useMemo(() => {
    const userGroups =
      getAdminSessionState.data?.admin?.user_details?.groups || [];
    return userGroups.some((group) => group.id === 1 || group.id === 20);
  }, [getAdminSessionState.data?.admin?.user_details?.groups]);
  const isAdminCheckComplete = getAdminSessionState.data !== undefined;

  const { availableStatuses } = giftCertificateState;

  const { validNextStatuses, pendingRequests } = giftCertificateState;

  const { userPermissions, canSeeCreatedGCs } = giftCertificateState;

  const isWarehouse = userPermissions?.is_warehouse ?? false;

  const [filters, setFilters] = useState({
    type: "",
    status: "",
    store_id: "",
    serial_number: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedGC, setSelectedGC] = useState<GiftCertificate | null>(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGCs, setSelectedGCs] = useState<number[]>([]);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>("Released");
  const [bulkStatusNotes, setBulkStatusNotes] = useState("");
  const [gcToDelete, setGcToDelete] = useState<GiftCertificate | null>(null);
  const [canSeeRedeemApprovals, setCanSeeRedeemApprovals] = useState(false);
  const [canApproveRedemption, setCanApproveRedemption] = useState(false);
  const [redeemedAtStore, setRedeemedAtStore] = useState<number | null>(null);
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [bulkRedeemedAtStore, setBulkRedeemedAtStore] = useState<number | null>(
    null
  );
  const [showBulkStoreSelection, setShowBulkStoreSelection] = useState(false);

  const [redemptionDate, setRedemptionDate] = useState<string>("");
  const [bulkRedemptionDate, setBulkRedemptionDate] = useState<string>("");

  // ADD: Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // ADD: Bulk approval states
  const [bulkApprovalDialogOpen, setBulkApprovalDialogOpen] = useState(false);
  const [bulkDeclineDialogOpen, setBulkDeclineDialogOpen] = useState(false);
  const [bulkDeclineLoading, setBulkDeclineLoading] = useState(false);
  const [selectedApprovalRequests, setSelectedApprovalRequests] = useState<
    number[]
  >([]);
  const [bulkApprovalLoading, setBulkApprovalLoading] = useState(false);

  // EXISTING: Status change workflow states
  const [statusConfirmDialog, setStatusConfirmDialog] = useState(false);
  const [statusRequestDialog, setStatusRequestDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    gc: any;
    newStatus: string;
  } | null>(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [reviewAction, setReviewAction] = useState<"approved" | "rejected">(
    "approved"
  );
  const [reviewNotes, setReviewNotes] = useState("");

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    name: string;
  } | null>(null);

  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([
    {
      key: "serial_number",
      label: "Serial Number",
      selected: true,
      mandatory: true,
    },
    { key: "type", label: "Type", selected: true, mandatory: true },
    {
      key: "store_company",
      label: "Store/Group",
      selected: true,
      mandatory: true,
    },
    { key: "company", label: "Company", selected: true, mandatory: true },
    { key: "status", label: "Status", selected: true, mandatory: true },
    {
      key: "redemption_date",
      label: "Redemption Date",
      selected: true,
      mandatory: true,
    },
    {
      key: "redeemed_at",
      label: "Redeemed At",
      selected: true,
      mandatory: true,
    },
    { key: "expiry", label: "Expiry", selected: true, mandatory: false },
    { key: "created", label: "Created", selected: true, mandatory: false },
    // NEW: Status history columns
    {
      key: "receiving_request",
      label: "Receiving Request",
      selected: false,
      mandatory: false,
    },
    {
      key: "receiving_approval",
      label: "Receiving Approval",
      selected: false,
      mandatory: false,
    },
    {
      key: "selling_request",
      label: "Selling Request",
      selected: false,
      mandatory: false,
    },
    {
      key: "selling_approval",
      label: "Selling Approval",
      selected: false,
      mandatory: false,
    },
    {
      key: "redemption_request",
      label: "Redemption Request",
      selected: false,
      mandatory: false,
    },
    {
      key: "redemption_approval",
      label: "Redemption Approval",
      selected: false,
      mandatory: false,
    },
  ]);

  const [columnAnchorEl, setColumnAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOption, setExportOption] = useState<
    "all-columns" | "with-history"
  >("all-columns");
  const [exportScope, setExportScope] = useState<
    "all" | "current-page" | "multiple-pages"
  >("all");
  const [exportPageCount, setExportPageCount] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const isApprovalStatus = (status: string) => {
    return status.includes("Approval");
  };

  // ADD: Get next status in sequence
  const getNextStatus = (currentStatus: string): string | null => {
    const statusFlow = {
      Created: "Released",
      Released: "Received",
      Received: "Sold",
      Sold: "Redeemed",
    };
    return statusFlow[currentStatus as keyof typeof statusFlow] || null;
  };

  // ADD: Check if status change requires approval
  const requiresApproval = (
    currentStatus: string,
    newStatus: string
  ): boolean => {
    // Released doesn't require approval
    if (newStatus === "Released") return false;

    // Other status changes require approval for stock clerks
    return Boolean(
      userPermissions?.is_stock_clerk && !userPermissions?.can_approve_status
    );
  };

  // UPDATED: Enhanced permission checking
  const hasEditPermission = (certificate: GiftCertificate) => {
    const isExpiredCert = isExpired(certificate.expiry_date);
    const isApproval = isApprovalStatus(certificate.current_status);
    const isRedeemed = certificate.current_status === "Redeemed";

    // ADDED: Check if user can see this GC (Created status check)
    if (!canSeeCreatedGCs && certificate.current_status === "Created") {
      return false;
    }

    if (isExpiredCert || isApproval || isRedeemed) {
      return false;
    }

    // UPDATED: More comprehensive permission check
    return (
      userPermissions?.can_approve_status ||
      userPermissions?.is_stock_clerk ||
      is_admin
    );
  };

  // ADDED: Get available status options based on permissions
  const getAvailableStatusOptions = () => {
    const allStatuses = [
      { value: "", label: "All Statuses" },
      { value: "Created", label: STATUS_LABELS.Created },
      { value: "Released", label: STATUS_LABELS.Released },
      { value: "Receive Approval", label: STATUS_LABELS["Receive Approval"] },
      { value: "Received", label: STATUS_LABELS.Received },
      { value: "Sold Approval", label: STATUS_LABELS["Sold Approval"] },
      { value: "Sold", label: STATUS_LABELS.Sold },
      { value: "Redeem Approval", label: STATUS_LABELS["Redeem Approval"] },
      { value: "Redeemed", label: STATUS_LABELS.Redeemed },
      { value: "Expired", label: STATUS_LABELS.Expired },
    ];

    // ADDED: Filter out "Created" for restricted users
    if (!canSeeCreatedGCs) {
      return allStatuses.filter((status) => status.value !== "Created");
    }

    return allStatuses;
  };

  // UPDATED: Enhanced useEffect to get permissions
  useEffect(() => {
    dispatch(getDropdownData());
    dispatch(getGiftCertificates(filters));
    dispatch(getUserStores());
    dispatch(getUserPermissions());
    dispatch(getAvailableStatuses());
  }, [dispatch]);

  useEffect(() => {
    if (userPermissions?.can_approve_status && !isWarehouse) {
      dispatch(getPendingRequests());
    }
  }, [dispatch, userPermissions?.can_approve_status, isWarehouse]);

  useEffect(() => {
    dispatch(getUserPermissions()).then((result) => {
      if (result.payload) {
        const permissions = result.payload as UserPermissions;
        setCanSeeRedeemApprovals(permissions.can_see_redeem_approvals || false);
        setCanApproveRedemption(permissions.can_approve_redemption || false);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (giftCertificateState.status === GiftCertificateState.success) {
      setTimeout(() => {
        dispatch(resetGiftCertificate());
      }, 3000);
    }
  }, [giftCertificateState.status, dispatch]);

  // UPDATED: Handle the list response to get permission info
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let finalFilters = { ...filters };

      if (activeTab === 1) {
        finalFilters.status = "Redeemed";
      }

      dispatch(getGiftCertificates(finalFilters));
      setPage(0);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dispatch, filters, activeTab]);

  useEffect(() => {
    setSelectedGCs([]);
  }, [activeTab]);

  useEffect(() => {
    return () => {
      setBulkStatusLoading(false);
      setBulkDeleteLoading(false);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      if (filters.status && filters.status !== "Redeemed") {
        setFilters((prev) => ({ ...prev, status: "" }));
      }
    }
  }, [activeTab]);

  // UPDATED: Enhanced sorting function with multi-column default sort
  const sortData = (
    data: GiftCertificate[],
    config: SortConfig | null
  ): GiftCertificate[] => {
    if (!data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      // If no specific sort is applied, use default multi-column sort
      if (!config) {
        // Default sort: First by created_at (desc), then by serial_number (desc)

        // Primary sort: created_at (descending - newest first)
        const aDate = new Date(a.created_at).getTime();
        const bDate = new Date(b.created_at).getTime();

        if (aDate !== bDate) {
          return bDate - aDate; // Descending order (newest first)
        }

        // Secondary sort: serial_number (descending - highest first)
        const aSerial = parseInt(a.serial_number);
        const bSerial = parseInt(b.serial_number);
        return bSerial - aSerial; // Descending order (highest first)
      }

      // Handle explicit column sorting
      let aVal: any, bVal: any;

      switch (config.key) {
        case "serial_number":
          aVal = parseInt(a.serial_number);
          bVal = parseInt(b.serial_number);
          break;
        case "expiry_date":
          aVal = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
          bVal = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
          break;
        case "created_at":
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case "redemption_date": // NEW: Add redemption date sorting
          aVal = a.redemption_date ? new Date(a.redemption_date).getTime() : 0;
          bVal = b.redemption_date ? new Date(b.redemption_date).getTime() : 0;
          break;
        case "status":
          aVal = STATUS_ORDER.indexOf(a.current_status);
          bVal = STATUS_ORDER.indexOf(b.current_status);
          break;
        case "store_company":
          // Handle all three types properly
          if (a.type === "support") {
            aVal = a.store_name || "";
          } else {
            aVal = a.group_name || ""; // customer and sponsorship use group_name
          }

          if (b.type === "support") {
            bVal = b.store_name || "";
          } else {
            bVal = b.group_name || ""; // customer and sponsorship use group_name
          }

          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
          break;
        default:
          return 0;
      }

      // Apply the explicit sort direction
      if (aVal < bVal) return config.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return config.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // ADD: Handle column sort
  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";

    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  // Fix the filtered variable typing issue:
  const getFilteredCertificates = (): GiftCertificate[] => {
    const certificates = giftCertificateState.certificates;
    if (!certificates || !Array.isArray(certificates)) {
      return [];
    }

    let filtered: GiftCertificate[];

    if (activeTab === 0) {
      filtered = certificates.filter(
        (cert) => cert.current_status !== "Redeemed"
      );
    } else if (activeTab === 1) {
      filtered = certificates.filter(
        (cert) => cert.current_status === "Redeemed"
      );
    } else {
      filtered = [];
    }

    // ADDED: Client-side filtering for Created GCs (backup to backend filtering)
    if (!canSeeCreatedGCs) {
      filtered = filtered.filter((cert) => cert.current_status !== "Created");
    }

    return sortData(filtered, sortConfig);
  };

  // ADD: Missing submitStatusUpdate function
  const submitStatusUpdate = () => {
    if (!selectedGC || !newStatus) return;

    dispatch(
      updateGCStatus({
        gc_id: selectedGC.id,
        status: newStatus,
        notes: statusNotes || undefined,
      })
    ).then(() => {
      dispatch(getGiftCertificates(filters));
      setStatusDialogOpen(false);
      setSelectedGC(null);
      setNewStatus("Released");
      setStatusNotes("");
    });
  };

  // UPDATED: Filter selectable GCs with permission check
  const getSelectableGCs = () => {
    return paginatedCertificates.filter(
      (cert) =>
        !isExpired(cert.expiry_date) &&
        !isApprovalStatus(cert.current_status) &&
        (canSeeCreatedGCs || cert.current_status !== "Created")
    );
  };

  const filteredCertificates = getFilteredCertificates();
  const paginatedCertificates = filteredCertificates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
    setSortConfig(null); // Reset sorting when changing tabs

    if (newValue === 2 && userPermissions?.can_approve_status) {
      dispatch(getPendingRequests());
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    if (field === "status" && activeTab === 1) {
      return;
    }

    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    const clearedFilters = {
      type: "",
      status: activeTab === 1 ? "Redeemed" : "",
      store_id: "",
      serial_number: "",
    };
    setFilters(clearedFilters);
    setPage(0);
  };

  // UPDATED: Enhanced edit status with new flow
  const handleEditStatus = (certificate: GiftCertificate) => {
    if (isExpired(certificate.expiry_date)) {
      alert("This gift certificate has expired and cannot be edited.");
      return;
    }

    if (isApprovalStatus(certificate.current_status)) {
      alert(
        "This certificate is currently awaiting approval and cannot be edited."
      );
      return;
    }

    if (!userPermissions) {
      alert("Unable to determine user permissions.");
      return;
    }

    // Get next status in sequence
    const nextStatus = getNextStatus(certificate.current_status);

    if (!nextStatus) {
      alert(
        `No further status changes available for status "${certificate.current_status}"`
      );
      return;
    }

    const isRedeemedStatus = nextStatus === "Redeemed";

    // UPDATED: Enhanced approval logic that handles all cases
    const needsApproval =
      // Original logic: Stock clerks need approval for everything (except Released)
      requiresApproval(certificate.current_status, nextStatus) ||
      // NEW logic: Everyone (including Managers/Team Captains) needs approval for redemption
      (isRedeemedStatus && !canApproveRedemption);

    const needsStoreSelection = nextStatus === "Redeemed";

    if (needsStoreSelection) {
      // Set default store if user has only one
      if (giftCertificateState.userStores?.length === 1) {
        setRedeemedAtStore(giftCertificateState.userStores[0].id);
      } else {
        setRedeemedAtStore(null);
      }
      setShowStoreSelection(true);
    } else {
      setShowStoreSelection(false);
      setRedeemedAtStore(null);
    }

    // UPDATED: Handle all approval cases properly
    if (needsApproval) {
      // Anyone who needs approval (Stock Clerks for everything, Managers/Team Captains for redemption only)
      setPendingStatusChange({
        gc: certificate,
        newStatus: nextStatus,
      });
      setStatusRequestDialog(true);
    } else if (
      userPermissions.can_approve_status ||
      is_admin ||
      (!needsApproval && userPermissions.is_stock_clerk) // Stock clerks can directly change to Released
    ) {
      // Direct status change (no approval needed)
      setPendingStatusChange({
        gc: certificate,
        newStatus: nextStatus,
      });
      setStatusConfirmDialog(true);
    } else {
      alert("You do not have permission to change or request status changes.");
    }
  };

  const handleViewHistory = (gc: GiftCertificate) => {
    setSelectedGC(gc);
    dispatch(getGCDetails(gc.id));
    setHistoryDialogOpen(true);
    if (is_admin) {
      dispatch(getGCAttachments(gc.id));
    }

    setHistoryDialogOpen(true);
  };

  const handleImageClick = (attachment: any) => {
    setSelectedImage({
      src: `${process.env.REACT_APP_DOMAIN_URL}api/gift-certificate/serve-image/${attachment.id}`,
      name: attachment.file_name,
    });
    setImageModalOpen(true);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.style.display = "none";
    // Show a placeholder or error message
    const parent = img.parentElement;
    if (parent) {
      const errorDiv = document.createElement("div");
      errorDiv.className =
        "bg-gray-100 border border-gray-300 rounded p-4 text-center text-gray-500";
      errorDiv.innerHTML =
        '<div class="text-sm">Image preview unavailable</div>';
      parent.appendChild(errorDiv);
    }
  };

  // UPDATED: Confirm direct status change
  const confirmStatusChange = () => {
    if (pendingStatusChange) {
      const updateData: any = {
        gc_id: pendingStatusChange.gc.id,
        status: pendingStatusChange.newStatus,
        notes:
          statusNotes ||
          `Status progression: ${pendingStatusChange.gc.current_status} → ${pendingStatusChange.newStatus}`,
      };

      // Add redeemed at store if applicable
      if (pendingStatusChange.newStatus === "Redeemed") {
        if (redeemedAtStore) {
          updateData.redeemed_at_store_id = redeemedAtStore;
        }
        if (redemptionDate) {
          updateData.redemption_date = redemptionDate;
        }
      }

      dispatch(updateGCStatus(updateData)).then(() => {
        setStatusConfirmDialog(false);
        setPendingStatusChange(null);
        setStatusNotes("");
        setRedeemedAtStore(null);
        setRedemptionDate("");
        setShowStoreSelection(false);
        const tabFilters = {
          ...filters,
          status: activeTab === 0 ? "" : "Redeemed",
        };
        dispatch(getGiftCertificates(tabFilters));
      });
    }
  };

  // UPDATED: Submit status change request
  const submitStatusRequest = () => {
    if (pendingStatusChange) {
      const requestData: any = {
        gc_id: pendingStatusChange.gc.id,
        status: pendingStatusChange.newStatus,
        notes:
          statusNotes ||
          `Requesting status change: ${pendingStatusChange.gc.current_status} → ${pendingStatusChange.newStatus}`,
      };

      if (pendingStatusChange.newStatus === "Redeemed") {
        if (redeemedAtStore) {
          requestData.redeemed_at_store_id = redeemedAtStore;
        }
        if (redemptionDate) {
          requestData.redemption_date = redemptionDate;
        }
      }

      dispatch(requestStatusChange(requestData))
        .then((result) => {
          setStatusRequestDialog(false);
          setPendingStatusChange(null);
          setStatusNotes("");
          setRedeemedAtStore(null);
          setRedemptionDate("");
          setShowStoreSelection(false);
          alert("Status change request submitted for approval.");

          const tabFilters = {
            ...filters,
            status: activeTab === 0 ? "" : "Redeemed",
          };
          dispatch(getGiftCertificates(tabFilters));
        })
        .catch((error) => {
          console.error("⚠ Status request error:", error);
          alert("Failed to submit status change request.");
        });
    }
  };

  const handleReview = (request: any, action: "approved" | "rejected") => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes("");
    setReviewDialog(true);
  };

  const submitReview = () => {
    if (selectedRequest) {
      dispatch(
        reviewStatusRequest({
          request_id: selectedRequest.id,
          action: reviewAction,
          review_notes: reviewNotes,
        })
      ).then(() => {
        setReviewDialog(false);
        setSelectedRequest(null);
        setReviewNotes("");
        dispatch(getPendingRequests());
        dispatch(getGiftCertificates(filters));
      });
    }
  };

  // ADD: Bulk approval functionality
  const handleBulkApproval = () => {
    setBulkApprovalDialogOpen(true);
  };

  const submitBulkApproval = async () => {
    setBulkApprovalLoading(true);

    try {
      await dispatch(
        bulkApproveRequests({
          request_ids: selectedApprovalRequests,
          review_notes: reviewNotes || "Bulk approval",
        })
      );

      setBulkApprovalDialogOpen(false);
      setSelectedApprovalRequests([]);
      setReviewNotes("");
      dispatch(getPendingRequests());
      dispatch(getGiftCertificates(filters));
    } catch (error) {
      console.error("Bulk approval failed:", error);
    } finally {
      setBulkApprovalLoading(false);
    }
  };

  // UPDATED: Use filtered list for select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableGCs = getSelectableGCs().map((cert) => cert.id);
      setSelectedGCs(selectableGCs);
    } else {
      setSelectedGCs([]);
    }
  };

  // UPDATED: Enhanced bulk selection with permission checks
  const handleSelectGC = (gcId: number, checked: boolean) => {
    const certificate = giftCertificateState.certificates?.find(
      (cert: GiftCertificate) => cert.id === gcId
    );

    if (certificate) {
      // ADDED: Check Created status permission
      if (!canSeeCreatedGCs && certificate.current_status === "Created") {
        return;
      }

      if (
        isExpired(certificate.expiry_date) ||
        isApprovalStatus(certificate.current_status)
      ) {
        return;
      }
    }

    if (checked) {
      setSelectedGCs((prev) => [...prev, gcId]);
    } else {
      setSelectedGCs((prev) => prev.filter((id) => id !== gcId));
    }
  };

  const isAllSelected = () => {
    const selectableGCs = getSelectableGCs().map((cert) => cert.id);
    return (
      selectableGCs.length > 0 &&
      selectableGCs.every((id) => selectedGCs.includes(id))
    );
  };

  const isIndeterminate = () => {
    const selectableGCs = getSelectableGCs().map((cert) => cert.id);
    return selectedGCs.length > 0 && selectedGCs.length < selectableGCs.length;
  };

  const getSelectedCertificates = () => {
    const certificates = giftCertificateState.certificates;
    if (!certificates || !Array.isArray(certificates)) {
      return [];
    }
    return certificates.filter((cert) => selectedGCs.includes(cert.id));
  };

  // Update this function to detect when redeemed status is involved
  const handleBulkStatusUpdate = () => {
    if (!getSelectedCertificates().length) return;

    const selectedCertificates = getSelectedCertificates();

    // Check if any certificates will move to "Redeemed" status (for store selection UI)
    const willHaveRedeemedStatus = selectedCertificates.some((cert) => {
      const nextStatus = getNextStatus(cert.current_status);
      return nextStatus === "Redeemed";
    });

    if (willHaveRedeemedStatus) {
      // Set default store if user has only one
      if (giftCertificateState.userStores?.length === 1) {
        setBulkRedeemedAtStore(giftCertificateState.userStores[0].id);
      } else {
        setBulkRedeemedAtStore(null);
      }
      setShowBulkStoreSelection(true);
    } else {
      setShowBulkStoreSelection(false);
      setBulkRedeemedAtStore(null);
    }

    // Always open dialog - the individual approval logic is handled in submitBulkStatusUpdate
    setBulkStatusDialogOpen(true);
  };

  // Submit bulk status update - sequential logic
  const submitBulkStatusUpdate = async () => {
    setBulkStatusLoading(true);

    try {
      const selectedCertificates = getSelectedCertificates();

      // Separate certificates into two groups: those needing approval vs direct updates
      const certificatesNeedingApproval: GiftCertificate[] = [];
      const certificatesForDirectUpdate: GiftCertificate[] = [];

      selectedCertificates.forEach((cert) => {
        const nextStatus = getNextStatus(cert.current_status);
        if (!nextStatus) return;

        const isRedeemedStatus = nextStatus === "Redeemed";

        // Check if THIS specific certificate needs approval
        const needsApproval =
          requiresApproval(cert.current_status, nextStatus) ||
          (isRedeemedStatus && !canApproveRedemption);

        if (needsApproval) {
          certificatesNeedingApproval.push(cert);
        } else {
          certificatesForDirectUpdate.push(cert);
        }
      });

      // Process certificates that need approval (requests)
      if (certificatesNeedingApproval.length > 0) {
        const requestPromises = certificatesNeedingApproval.map((cert) => {
          const nextStatus = getNextStatus(cert.current_status);
          if (!nextStatus) return Promise.resolve();

          const requestData: any = {
            gc_id: cert.id,
            status: nextStatus,
            notes:
              bulkStatusNotes ||
              `Bulk status change request: ${cert.current_status} → ${nextStatus}`,
          };

          // Add store selection and redemption date if the next status is Redeemed
          if (nextStatus === "Redeemed") {
            if (bulkRedeemedAtStore) {
              requestData.redeemed_at_store_id = bulkRedeemedAtStore;
            }
            if (bulkRedemptionDate) {
              requestData.redemption_date = bulkRedemptionDate;
            }
          }

          return dispatch(requestStatusChange(requestData));
        });

        await Promise.all(requestPromises);
      }

      // Process certificates that can be updated directly
      if (certificatesForDirectUpdate.length > 0) {
        const updatePromises = certificatesForDirectUpdate.map((cert) => {
          const nextStatus = getNextStatus(cert.current_status);
          if (!nextStatus) return Promise.resolve();

          const updateData: any = {
            gc_id: cert.id,
            status: nextStatus,
            notes:
              bulkStatusNotes ||
              `Bulk update: ${cert.current_status} → ${nextStatus}`,
          };

          // Add store selection and redemption date if the next status is Redeemed
          if (nextStatus === "Redeemed") {
            if (bulkRedeemedAtStore) {
              updateData.redeemed_at_store_id = bulkRedeemedAtStore;
            }
            if (bulkRedemptionDate) {
              updateData.redemption_date = bulkRedemptionDate;
            }
          }

          return dispatch(updateGCStatus(updateData));
        });

        await Promise.all(updatePromises);
      }

      // Show appropriate success message
      let message = "";
      if (
        certificatesForDirectUpdate.length > 0 &&
        certificatesNeedingApproval.length > 0
      ) {
        message = `${certificatesForDirectUpdate.length} certificate(s) updated directly, ${certificatesNeedingApproval.length} certificate(s) sent for approval.`;
      } else if (certificatesForDirectUpdate.length > 0) {
        message = `${certificatesForDirectUpdate.length} certificate(s) updated successfully.`;
      } else if (certificatesNeedingApproval.length > 0) {
        const hasRedemptionRequests = certificatesNeedingApproval.some(
          (cert) => getNextStatus(cert.current_status) === "Redeemed"
        );
        if (hasRedemptionRequests && !canApproveRedemption) {
          message = `${certificatesNeedingApproval.length} certificate(s) sent for approval. Redemption requests require administrator approval.`;
        } else {
          message = `${certificatesNeedingApproval.length} certificate(s) sent for approval.`;
        }
      }

      if (message) {
        alert(message);
      }

      setBulkStatusDialogOpen(false);
      setBulkStatusNotes("");
      setBulkRedeemedAtStore(null);
      setBulkRedemptionDate(""); // ADDED: Clear bulk redemption date
      setShowBulkStoreSelection(false);
      setSelectedGCs([]);

      const tabFilters = {
        ...filters,
        status: activeTab === 0 ? "" : "Redeemed",
      };
      dispatch(getGiftCertificates(tabFilters));
    } catch (error) {
      console.error("Bulk status update failed:", error);
    } finally {
      setBulkStatusLoading(false);
    }
  };

  // NEW: Bulk decline functionality
  const handleBulkDecline = () => {
    setBulkDeclineDialogOpen(true);
  };

  const submitBulkDecline = async () => {
    setBulkDeclineLoading(true);

    try {
      // Process each selected request for rejection
      const declinePromises = selectedApprovalRequests.map((requestId) => {
        return dispatch(
          reviewStatusRequest({
            request_id: requestId,
            action: "rejected",
            review_notes: reviewNotes || "Bulk declined",
          })
        );
      });

      await Promise.all(declinePromises);

      setBulkDeclineDialogOpen(false);
      setSelectedApprovalRequests([]);
      setReviewNotes("");
      dispatch(getPendingRequests());
      dispatch(getGiftCertificates(filters));
    } catch (error) {
      console.error("Bulk decline failed:", error);
    } finally {
      setBulkDeclineLoading(false);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const submitBulkDelete = async () => {
    setBulkDeleteLoading(true);

    try {
      const selectedCertificates = getSelectedCertificates();

      const deletePromises = selectedCertificates.map((cert) =>
        dispatch(deleteGiftCertificate(cert.id))
      );

      await Promise.all(deletePromises);

      setBulkDeleteDialogOpen(false);
      setSelectedGCs([]);

      const tabFilters = {
        ...filters,
        status: activeTab === 0 ? "" : "Redeemed",
      };
      dispatch(getGiftCertificates(tabFilters));
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleDeleteClick = (gc: GiftCertificate) => {
    setGcToDelete(gc);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!gcToDelete) return;

    dispatch(deleteGiftCertificate(gcToDelete.id)).then(() => {
      setDeleteDialogOpen(false);
      setGcToDelete(null);
      const tabFilters = {
        ...filters,
        status: activeTab === 0 ? "" : "Redeemed",
      };
      dispatch(getGiftCertificates(tabFilters));
    });
  };

  // UPDATED: Get status color with new statuses
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Created: "default",
      Released: "primary",
      "Receive Approval": "warning",
      Received: "info",
      "Sold Approval": "warning",
      Sold: "success",
      "Redeem Approval": "warning",
      Redeemed: "error",
      Expired: "error",
    };
    return colors[status] || "default";
  };

  // UPDATED: Get status icon with new statuses
  const getStatusIcon = (status: string): React.ReactElement => {
    const icons: Record<string, React.ReactElement> = {
      Created: <MdNewReleases size={16} />,
      Released: <MdEventAvailable size={16} />,
      "Receive Approval": <FaClock size={16} />,
      Received: <MdOutlineAssignment size={16} />,
      "Sold Approval": <FaClock size={16} />,
      Sold: <MdSell size={16} />,
      "Redeem Approval": <FaClock size={16} />,
      Redeemed: <MdRedeem size={16} />,
      Expired: <FaTimes size={16} />,
    };
    return icons[status] || <MdEventAvailable size={16} />;
  };

  // Add this helper function before your return statement
  const renderColumnValue = (
    certificate: GiftCertificate,
    columnKey: string
  ) => {
    const formatDate = (dateStr: string | null | undefined) => {
      if (!dateStr) return <span className="text-gray-400 text-sm">—</span>;
      return (
        <div className="text-sm">
          <div className="font-medium">
            {new Date(dateStr).toLocaleDateString("en-PH", {
              timeZone: "Asia/Manila",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(dateStr).toLocaleTimeString("en-PH", {
              timeZone: "Asia/Manila",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      );
    };

    switch (columnKey) {
      case "serial_number":
        return (
          <span className="font-semibold tracking-wide">
            #{certificate.serial_number}
          </span>
        );

      case "type":
        return (
          <Chip
            label={certificate.type}
            size="small"
            color={
              certificate.type === "customer"
                ? "success"
                : certificate.type === "sponsorship"
                ? "secondary"
                : // : certificate.type === "multiple"
                // ? "info"
                certificate.type === "support"
                ? "warning"
                : "default"
            }
          />
        );

      case "store_company":
        return (
          <div className="flex items-center space-x-1">
            {certificate.type === "support" ? (
              <>
                <FaStore size={14} className="text-green-600" />
                <span className="text-sm">{certificate.store_name}</span>
              </>
            ) : (
              <>
                <FaBuilding size={14} className="text-purple-600" />
                <span className="text-sm">{certificate.group_name}</span>
              </>
            )}
          </div>
        );

      case "company":
        return certificate.type === "sponsorship" ? (
          <div className="flex items-center space-x-1">
            <FaBuilding size={14} className="text-purple-600" />
            <span className="text-sm">{certificate.company_name}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        );

      case "status":
        return (
          <div className="flex items-center space-x-2">
            <Chip
              label={
                STATUS_LABELS[
                  certificate.current_status as keyof typeof STATUS_LABELS
                ] || certificate.current_status
              }
              size="small"
              color={getStatusColor(certificate.current_status) as any}
              icon={getStatusIcon(certificate.current_status)}
            />
            <StatusTooltip status={certificate.current_status} />
            {isApprovalStatus(certificate.current_status) && (
              <Tooltip title="This certificate is awaiting approval">
                <div className="animate-pulse">
                  <FaClock size={12} className="text-orange-500" />
                </div>
              </Tooltip>
            )}
          </div>
        );

      case "redemption_date":
        return formatDate(certificate.redemption_date);

      case "redeemed_at":
        return certificate.redeemed_at_store_name ? (
          <div className="flex items-center">
            <FaStore size={14} className="mr-2 text-green-600" />
            <span className="text-sm">
              {certificate.redeemed_at_store_name}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        );

      case "expiry":
        return certificate.expiry_date ? (
          <div className="flex items-center space-x-1">
            <FaCalendarCheck
              size={14}
              className={
                isExpired(certificate.expiry_date)
                  ? "text-red-700"
                  : isExpiringToday(certificate.expiry_date)
                  ? "text-red-600"
                  : isExpiringSoon(certificate.expiry_date)
                  ? "text-orange-500"
                  : "text-gray-400"
              }
            />
            <span
              className={`text-sm ${
                isExpired(certificate.expiry_date)
                  ? "text-red-700 font-semibold"
                  : isExpiringToday(certificate.expiry_date)
                  ? "text-red-600 font-semibold"
                  : isExpiringSoon(certificate.expiry_date)
                  ? "text-orange-600"
                  : "text-gray-600"
              }`}
            >
              {new Date(certificate.expiry_date).toLocaleDateString()}
            </span>
            {isExpired(certificate.expiry_date) && (
              <Chip label="Expired" size="small" color="error" />
            )}
            {!isExpired(certificate.expiry_date) &&
              isExpiringToday(certificate.expiry_date) && (
                <Chip label="Expires Today" size="small" color="error" />
              )}
            {!isExpired(certificate.expiry_date) &&
              !isExpiringToday(certificate.expiry_date) &&
              isExpiringSoon(certificate.expiry_date) && (
                <Chip label="Expiring Soon" size="small" color="warning" />
              )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">No expiry</span>
        );

      case "created":
        return (
          <div className="text-sm text-gray-600">
            {new Date(certificate.created_at).toLocaleDateString()}
            <br />
            <span className="text-xs">
              by {certificate.first_name} {certificate.last_name}
            </span>
          </div>
        );

      case "receiving_request":
        return formatDate(certificate.receiving_request_date);

      case "receiving_approval":
        return formatDate(certificate.receiving_approval_date);

      case "selling_request":
        return formatDate(certificate.selling_request_date);

      case "selling_approval":
        return formatDate(certificate.selling_approval_date);

      case "redemption_request":
        return formatDate(certificate.redemption_request_date);

      case "redemption_approval":
        return formatDate(certificate.redemption_approval_date);

      default:
        return null;
    }
  };

  const certificate = giftCertificateState.certificates;
  const dropdownData = giftCertificateState.dropdownData;
  const statusHistory = giftCertificateState.statusHistory;
  const currentCertificate = giftCertificateState.currentCertificate;

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;

    const today = new Date();
    const expiry = new Date(expiryDate);

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const result = today > expiry;
    return result;
  };

  const isExpiringToday = (expiryDate?: string) => {
    if (!expiryDate) return false;

    const today = new Date();
    const expiry = new Date(expiryDate);

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const result = today.getTime() === expiry.getTime();
    return result;
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;

    const expiry = new Date(expiryDate);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const result = diffDays <= 30 && diffDays >= 0;
    return result;
  };

  // ADD: Handle approval request selection
  const handleSelectApprovalRequest = (requestId: number, checked: boolean) => {
    if (checked) {
      setSelectedApprovalRequests((prev: any) => [...prev, requestId]);
    } else {
      setSelectedApprovalRequests((prev: any[]) =>
        prev.filter((id: number) => id !== requestId)
      );
    }
  };

  const handleSelectAllApprovalRequests = (checked: boolean) => {
    if (checked) {
      // Filter requests the same way as in renderPendingApprovalsTab
      const visibleRequests = pendingRequests.filter((request) => {
        if (!canSeeRedeemApprovals && request.requested_status === "Redeemed") {
          return false;
        }
        return true;
      });
      setSelectedApprovalRequests(visibleRequests.map((req) => req.id));
    } else {
      setSelectedApprovalRequests([]);
    }
  };

  // ADD: Render sort icon
  const renderSortIcon = (column: string) => {
    if (!sortConfig || sortConfig.key !== column) {
      return <FaSort className="opacity-30" size={12} />;
    }

    return sortConfig.direction === "asc" ? (
      <FaSortUp className="text-blue-500" size={12} />
    ) : (
      <FaSortDown className="text-blue-500" size={12} />
    );
  };

  // Export helper functions
  const exportToExcel = async () => {
    if (!is_admin) {
      alert("Access denied. Only administrators can export data.");
      return;
    }

    setIsExporting(true);

    try {
      const XLSX = await import("xlsx-js-style");

      // Determine which certificates to export
      let certificatesToExport: GiftCertificate[] = [];

      if (exportScope === "all") {
        certificatesToExport = filteredCertificates;
      } else if (exportScope === "current-page") {
        certificatesToExport = paginatedCertificates;
      } else if (exportScope === "multiple-pages") {
        const totalPages = Math.min(
          exportPageCount,
          Math.ceil(filteredCertificates.length / rowsPerPage)
        );
        certificatesToExport = filteredCertificates.slice(
          0,
          totalPages * rowsPerPage
        );
      }

      if (exportOption === "all-columns") {
        // Export with all selected columns
        await exportWithAllColumns(XLSX, certificatesToExport);
      } else {
        // Export with default columns + history sheet
        await exportWithHistory(XLSX, certificatesToExport);
      }

      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const exportWithAllColumns = async (
    XLSX: any,
    certificates: GiftCertificate[]
  ) => {
    const selectedColumns = columnOptions.filter((c) => c.selected);

    // Prepare data rows
    const data = certificates.map((cert) => {
      const row: any = {};

      selectedColumns.forEach((column) => {
        switch (column.key) {
          case "serial_number":
            row["Serial Number"] = cert.serial_number;
            break;
          case "type":
            row["Type"] = cert.type;
            break;
          case "store_company":
            row["Store/Group"] =
              cert.type === "customer" || cert.type === "support"
                ? cert.store_name
                : cert.group_name;
            break;
          case "company":
            row["Company"] =
              cert.type === "sponsorship" ? cert.company_name : "—";
            break;
          case "status":
            row["Status"] =
              STATUS_LABELS[
                cert.current_status as keyof typeof STATUS_LABELS
              ] || cert.current_status;
            break;
          case "redemption_date":
            row["Redemption Date"] = cert.redemption_date
              ? new Date(cert.redemption_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "redeemed_at":
            row["Redeemed At"] = cert.redeemed_at_store_name || "—";
            break;
          case "expiry":
            row["Expiry Date"] = cert.expiry_date
              ? new Date(cert.expiry_date).toLocaleDateString("en-PH")
              : "No expiry";
            break;
          case "created":
            row["Created Date"] = new Date(cert.created_at).toLocaleDateString(
              "en-PH"
            );
            row["Created By"] = `${cert.first_name} ${cert.last_name}`;
            break;
          case "receiving_request":
            row["Receiving Request Date"] = cert.receiving_request_date
              ? new Date(cert.receiving_request_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "receiving_approval":
            row["Receiving Approval Date"] = cert.receiving_approval_date
              ? new Date(cert.receiving_approval_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "selling_request":
            row["Selling Request Date"] = cert.selling_request_date
              ? new Date(cert.selling_request_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "selling_approval":
            row["Selling Approval Date"] = cert.selling_approval_date
              ? new Date(cert.selling_approval_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "redemption_request":
            row["Redemption Request Date"] = cert.redemption_request_date
              ? new Date(cert.redemption_request_date).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            break;
          case "redemption_approval":
            row["Redemption Approval Date"] = cert.redemption_approval_date
              ? new Date(cert.redemption_approval_date).toLocaleString(
                  "en-PH",
                  {
                    timeZone: "Asia/Manila",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )
              : "—";
            break;
        }
      });

      return row;
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    // Make headers bold
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;

      worksheet[cellAddress].s = {
        fill: {
          fgColor: { rgb: "C6EFCE" }, // Light green color
        },
        font: {
          bold: true,
          color: { rgb: "000000" }, // Black text
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Redeemed GCs");

    // Auto-size columns
    const colWidths = selectedColumns.map(() => ({ wch: 20 }));
    worksheet["!cols"] = colWidths;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const scopeLabel =
      exportScope === "all"
        ? "All"
        : exportScope === "current-page"
        ? "Page" + (page + 1)
        : exportPageCount + "Pages";
    const filename = `Redeemed_GCs_${scopeLabel}_${timestamp}.xlsx`;

    // Download
    XLSX.writeFile(workbook, filename);
  };

  const exportWithHistory = async (
    XLSX: any,
    certificates: GiftCertificate[]
  ) => {
    // Sheet 1: Default columns with bold headers
    const defaultData = certificates.map((cert) => ({
      "Serial Number": cert.serial_number,
      Type: cert.type,
      "Store/Group":
        cert.type === "customer" || cert.type === "support"
          ? cert.store_name
          : cert.group_name,
      Company: cert.type === "sponsorship" ? cert.company_name : "—",
      Status:
        STATUS_LABELS[cert.current_status as keyof typeof STATUS_LABELS] ||
        cert.current_status,
      "Redemption Date": cert.redemption_date
        ? new Date(cert.redemption_date).toLocaleString("en-PH", {
            timeZone: "Asia/Manila",
          })
        : "—",
      "Redeemed At": cert.redeemed_at_store_name || "—",
      "Expiry Date": cert.expiry_date
        ? new Date(cert.expiry_date).toLocaleDateString("en-PH")
        : "No expiry",
      "Created Date": new Date(cert.created_at).toLocaleDateString("en-PH"),
      "Created By": `${cert.first_name} ${cert.last_name}`,
    }));

    // Sheet 2: Complete history for each GC (one row per status)
    const historyData: any[] = [];

    // Fetch history for all certificates sequentially
    for (const cert of certificates) {
      try {
        // Fetch history for this certificate
        const result: any = await dispatch(getGCDetails(cert.id));

        // Extract history from the action result
        let history: StatusHistory[] = [];

        if (result.payload?.data?.history) {
          history = result.payload.data.history;
        } else if (result.payload?.history) {
          history = result.payload.history;
        }

        if (history.length > 0) {
          // Sort history by date (oldest first)
          const sortedHistory = [...history].sort(
            (a, b) =>
              new Date(a.changed_at).getTime() -
              new Date(b.changed_at).getTime()
          );

          sortedHistory.forEach((h: StatusHistory, index: number) => {
            const isApprovalStatus = h.status.includes("Approval");
            const isFirstRow = index === 0;
            const isCreatedStatus = h.status === "Created" || h.status === "";

            historyData.push({
              "Serial Number": isFirstRow
                ? cert.serial_number
                : cert.serial_number, // Always include serial
              Type: isFirstRow ? cert.type : cert.type, // Always include type
              Status: h.status,
              "Requested At": isApprovalStatus
                ? new Date(h.changed_at).toLocaleString("en-PH", {
                    timeZone: "Asia/Manila",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—",
              "Changed At": !isApprovalStatus
                ? new Date(h.changed_at).toLocaleString("en-PH", {
                    timeZone: "Asia/Manila",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—",
              "Changed By": `${h.first_name} ${h.last_name}`,
              Notes: h.notes || "—",
              // NEW: Hidden flag for styling
              _isCreatedStatus: isCreatedStatus,
              _isFirstRow: isFirstRow,
            });
          });
        } else {
          // If no history, add a placeholder row
          historyData.push({
            "Serial Number": cert.serial_number,
            Type: cert.type,
            Status: "No history available",
            "Requested At": "—",
            "Changed At": "—",
            "Changed By": "—",
            Notes: "—",
            _isCreatedStatus: false,
            _isFirstRow: true,
          });
        }
      } catch (error) {
        console.error(
          `Failed to fetch history for GC ${cert.serial_number}:`,
          error
        );
        // Add error row
        historyData.push({
          "Serial Number": cert.serial_number,
          Type: cert.type,
          Status: "Error loading history",
          "Requested At": "—",
          "Changed At": "—",
          "Changed By": "—",
          Notes: "—",
          _isCreatedStatus: false,
          _isFirstRow: true,
        });
      }
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Default columns
    const worksheet1 = XLSX.utils.json_to_sheet(defaultData);

    // Make Sheet 1 headers bold
    const sheet1Range = XLSX.utils.decode_range(worksheet1["!ref"] || "A1");
    for (let col = sheet1Range.s.c; col <= sheet1Range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet1[cellAddress]) continue;

      worksheet1[cellAddress].s = {
        fill: {
          fgColor: { rgb: "C6EFCE" }, // Light green
        },
        font: {
          bold: true,
          color: { rgb: "000000" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet1, "Redeemed GCs");

    // Sheet 2: Status History (with hidden serial/type for non-created statuses)
    // Remove the hidden flags before converting to sheet
    const cleanHistoryData = historyData.map((row) => {
      const { _isCreatedStatus, _isFirstRow, ...cleanRow } = row;
      return cleanRow;
    });

    const worksheet2 = XLSX.utils.json_to_sheet(cleanHistoryData);

    // Make Sheet 2 headers bold
    const sheet2Range = XLSX.utils.decode_range(worksheet2["!ref"] || "A1");
    for (let col = sheet2Range.s.c; col <= sheet2Range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet2[cellAddress]) continue;

      worksheet2[cellAddress].s = {
        fill: {
          fgColor: { rgb: "C6EFCE" }, // Light green
        },
        font: {
          bold: true,
          color: { rgb: "000000" },
        },
        alignment: {
          horizontal: "center",
          vertical: "center",
        },
      };
    }

    // Apply white font color to Serial Number and Type cells for non-created statuses
    historyData.forEach((row, index) => {
      const rowIndex = index + 1; // +1 because row 0 is headers

      // Apply white font to Serial Number (column A)
      if (!row._isCreatedStatus && !row._isFirstRow) {
        const serialCellAddress = XLSX.utils.encode_cell({
          r: rowIndex,
          c: 0,
        }); // Column A
        if (worksheet2[serialCellAddress]) {
          worksheet2[serialCellAddress].s = {
            font: {
              color: { rgb: "FFFFFF" }, // White font
            },
          };
        }

        // Apply white font to Type (column B)
        const typeCellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: 1 }); // Column B
        if (worksheet2[typeCellAddress]) {
          worksheet2[typeCellAddress].s = {
            font: {
              color: { rgb: "FFFFFF" }, // White font
            },
          };
        }
      }
    });

    XLSX.utils.book_append_sheet(workbook, worksheet2, "Status History");

    // Set column widths
    worksheet1["!cols"] = [
      { wch: 15 },
      { wch: 12 },
      { wch: 25 },
      { wch: 25 },
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];

    worksheet2["!cols"] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 40 },
    ];

    // Generate filename
    const timestamp = new Date().toISOString().split("T")[0];
    const scopeLabel =
      exportScope === "all"
        ? "All"
        : exportScope === "current-page"
        ? "Page" + (page + 1)
        : exportPageCount + "Pages";
    const filename = `Redeemed_GCs_WithHistory_${scopeLabel}_${timestamp}.xlsx`;

    // Download
    XLSX.writeFile(workbook, filename);
  };

  // UPDATED: Pending Approvals Tab with bulk selection
  const renderPendingApprovalsTab = () => {
    // Filter requests based on user permissions
    const visibleRequests = pendingRequests.filter((request) => {
      // If user can't see redeem approvals, filter out Redeemed requests
      if (!canSeeRedeemApprovals && request.requested_status === "Redeemed") {
        return false;
      }
      return true;
    });

    return (
      <div className="space-y-4">
        <Card>
          <CardContent
            sx={{
              py: { xs: 2, sm: 3 },
              px: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: { xs: "flex-start", sm: "space-between" },
                gap: { xs: 2, sm: 0 },
              }}
            >
              {/* Left side - Title and description */}
              <div>
                <Typography
                  variant="h6"
                  className="font-semibold flex items-center"
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  <FaClock className="mr-2 text-orange-500" />
                  Pending Status Change Approvals
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                >
                  Review and approve status change requests from staff
                  {!canSeeRedeemApprovals &&
                    " (Redemption requests require admin approval)"}
                </Typography>
              </div>

              {/* Right side - Action buttons and count */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                }}
              >
                {/* Action buttons - Only show when items are selected */}
                {selectedApprovalRequests.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {/* Bulk Approve Button */}
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<FaCheck />}
                      onClick={handleBulkApproval}
                      size="small"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        padding: { xs: "4px 8px", sm: "6px 12px" },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      APPROVE SELECTED
                    </Button>

                    {/* NEW: Bulk Decline Button */}
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<FaTimes />}
                      onClick={handleBulkDecline}
                      size="small"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        padding: { xs: "4px 8px", sm: "6px 12px" },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      DECLINE SELECTED
                    </Button>
                  </Box>
                )}

                {/* Pending count chip */}
                <Chip
                  label={`${visibleRequests.length} Pending`}
                  color={visibleRequests.length > 0 ? "warning" : "default"}
                  icon={<FaClock />}
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    height: { xs: 28, sm: 32 },
                    "& .MuiChip-label": {
                      paddingX: { xs: 1, sm: 1.5 },
                    },
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedApprovalRequests.length ===
                          visibleRequests.length && visibleRequests.length > 0
                      }
                      indeterminate={
                        selectedApprovalRequests.length > 0 &&
                        selectedApprovalRequests.length < visibleRequests.length
                      }
                      onChange={(e) =>
                        handleSelectAllApprovalRequests(e.target.checked)
                      }
                    />
                  </TableCell>
                  <TableCell>Certificate</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Store/Company</TableCell>
                  <TableCell>Current Status</TableCell>
                  <TableCell>Requested Status</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <Typography variant="body2" color="textSecondary">
                        {!canSeeRedeemApprovals && pendingRequests.length > 0
                          ? "All pending requests require admin approval"
                          : "No pending approval requests"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRequests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedApprovalRequests.includes(
                            request.id
                          )}
                          onChange={(e) =>
                            handleSelectApprovalRequest(
                              request.id,
                              e.target.checked
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <span className="tracking-wide font-semibold">
                          #{request.serial_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.type}
                          size="small"
                          color={
                            request.type === "customer"
                              ? "success"
                              : request.type === "sponsorship"
                              ? "secondary"
                              : // : request.type === "multiple"
                              // ? "info"
                              request.type === "support"
                              ? "warning"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {request.type === "customer" ||
                          request.type === "support"
                            ? request.store_name
                            : request.type === "sponsorship"
                            ? request.company_name
                            : request.group_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            STATUS_LABELS[
                              request.current_status as keyof typeof STATUS_LABELS
                            ] || request.current_status
                          }
                          size="small"
                          color={getStatusColor(request.current_status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            STATUS_LABELS[
                              request.requested_status as keyof typeof STATUS_LABELS
                            ] || request.requested_status
                          }
                          size="small"
                          color={
                            getStatusColor(request.requested_status) as any
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium text-sm">
                            {request.requester_first_name}{" "}
                            {request.requester_last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {request.request_notes && (
                              <Tooltip title={request.request_notes}>
                                <span>📝 Has notes</span>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Tooltip title="Approve Request" arrow>
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              onClick={() => handleReview(request, "approved")}
                              sx={{
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 3,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                boxShadow: 1,
                                "&:hover": {
                                  boxShadow: 2,
                                },
                              }}
                            >
                              Approve
                            </Button>
                          </Tooltip>
                          <Tooltip title="Reject Request" arrow>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => handleReview(request, "rejected")}
                              sx={{
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 3,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                textTransform: "none",
                                boxShadow: 1,
                                "&:hover": {
                                  boxShadow: 2,
                                },
                              }}
                            >
                              Reject
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
            <FaSearch className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Manage Gift Certificates
            </h1>
            <p className="text-gray-600">
              Search, filter, and update gift certificate statuses
            </p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {giftCertificateState.status === GiftCertificateState.success && (
        <Alert severity="success" className="mb-4">
          {giftCertificateState.message}
        </Alert>
      )}

      {giftCertificateState.status === GiftCertificateState.fail && (
        <Alert severity="error" className="mb-4">
          {giftCertificateState.message}
        </Alert>
      )}

      {/* Tabs */}
      <Card className="mb-6">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          className="border-b"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-scrollButtons": {
              "&.Mui-disabled": { opacity: 0.3 },
            },
            "& .MuiTab-root": {
              minWidth: { xs: 120, sm: "auto" },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
              padding: { xs: "8px 8px", sm: "12px 16px" },
            },
            "& .MuiTabs-flexContainer": {
              minWidth: "max-content",
            },
            "& .MuiTabs-scroller": {
              overflowX: "auto",
            },
          }}
        >
          <Tab
            label={
              <div className="flex items-center space-x-2">
                <span>Active Gift Certificates</span>
                <Chip
                  label={
                    (giftCertificateState.certificates || []).filter(
                      (cert) => cert.current_status !== "Redeemed"
                    ).length
                  }
                  size="small"
                  color="primary"
                />
              </div>
            }
          />
          <Tab
            label={
              <div className="flex items-center space-x-2">
                <span>Redeemed Gift Certificates</span>
                <Chip
                  label={
                    (giftCertificateState.certificates || []).filter(
                      (cert) => cert.current_status === "Redeemed"
                    ).length
                  }
                  size="small"
                  color="error"
                />
              </div>
            }
          />
          {userPermissions?.can_approve_status && !isWarehouse && (
            <Tab
              label={
                <div className="flex items-center space-x-2">
                  <span>Pending Approvals</span>
                  <Chip
                    label={pendingRequests.length}
                    size="small"
                    color={pendingRequests.length > 0 ? "warning" : "default"}
                  />
                </div>
              }
            />
          )}
        </Tabs>
      </Card>

      {/* Conditional Content */}
      {activeTab === 2 &&
      userPermissions?.can_approve_status &&
      !isWarehouse ? (
        renderPendingApprovalsTab()
      ) : (
        <>
          {/* Filters */}
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="mb-3 font-semibold">
                Search & Filter
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      onChange={(e) =>
                        handleFilterChange("type", e.target.value)
                      }
                      label="Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="customer">Customer</MenuItem>
                      <MenuItem value="sponsorship">Sponsorship</MenuItem>
                      {/* <MenuItem value="multiple">Multiple</MenuItem> */}
                      <MenuItem value="support">Support</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {activeTab === 0 && (
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={(e) =>
                          handleFilterChange("status", e.target.value)
                        }
                        label="Status"
                      >
                        {getAvailableStatusOptions().map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={activeTab === 0 ? 3 : 4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Store</InputLabel>
                    <Select
                      value={filters.store_id}
                      onChange={(e) =>
                        handleFilterChange("store_id", e.target.value)
                      }
                      label="Store"
                    >
                      <MenuItem value="">All Stores</MenuItem>
                      {giftCertificateState.userStores?.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={activeTab === 0 ? 3 : 4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Serial Number"
                    value={filters.serial_number}
                    onChange={(e) =>
                      handleFilterChange("serial_number", e.target.value)
                    }
                    placeholder="Search by serial number"
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MdClear />}
                    onClick={clearFilters}
                    size="small"
                  >
                    Clear All
                  </Button>
                </Grid>

                {activeTab === 1 && ( // Only show on Redeemed tab
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={(e) => setColumnAnchorEl(e.currentTarget)}
                      size="small"
                    >
                      Columns ({columnOptions.filter((c) => c.selected).length})
                    </Button>
                  </Grid>
                )}

                {activeTab === 1 && is_admin && (
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      startIcon={<FaDownload />}
                      onClick={() => setExportDialogOpen(true)}
                      size="small"
                    >
                      Export to Excel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Loading indicator */}
          {giftCertificateState.status === GiftCertificateState.inProgress && (
            <Card className="mb-4">
              <CardContent className="py-3">
                <div className="flex items-center justify-center">
                  <CircularProgress size={20} className="mr-2" />
                  <Typography variant="body2" color="textSecondary">
                    Searching gift certificates...
                  </Typography>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results Summary */}
          <Card className="mb-4">
            <CardContent
              sx={{
                py: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 3 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: { xs: "flex-start", sm: "space-between" },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                    color: "text.primary",
                  }}
                >
                  Showing {filteredCertificates.length} of{" "}
                  {(giftCertificateState.certificates || []).length} gift
                  certificates
                  {activeTab === 0 ? " (Active)" : " (Redeemed)"}
                </Typography>

                {/* Type Chips */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Chip
                    label={`Customer: ${
                      filteredCertificates.filter((c) => c.type === "customer")
                        .length
                    }`}
                    size="small"
                    color="success"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      height: { xs: 24, sm: 32 },
                    }}
                  />
                  <Chip
                    label={`Sponsorship: ${
                      filteredCertificates.filter(
                        (c) => c.type === "sponsorship"
                      ).length
                    }`}
                    size="small"
                    color="secondary"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      height: { xs: 24, sm: 32 },
                    }}
                  />
                  {/* <Chip
                    label={`Multiple: ${
                      filteredCertificates.filter((c) => c.type === "multiple")
                        .length
                    }`}
                    size="small"
                    color="info"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      height: { xs: 24, sm: 32 },
                    }}
                  /> */}
                  <Chip
                    label={`Support: ${
                      filteredCertificates.filter((c) => c.type === "support")
                        .length
                    }`}
                    size="small"
                    color="warning"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      height: { xs: 24, sm: 32 },
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Bulk Actions Toolbar */}
          {selectedGCs.length > 0 && (
            <Card className="mb-4">
              <Toolbar
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  borderLeft: 4,
                  borderLeftColor: "primary.main",
                  borderLeftStyle: "solid",
                  minHeight: { xs: "auto", sm: 64 },
                  padding: { xs: 2, sm: 3 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "flex-start", sm: "space-between" },
                    width: "100%",
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  {/* Selection Count and Type Breakdown */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", sm: "1.1rem" },
                      }}
                    >
                      {selectedGCs.length} certificate
                      {selectedGCs.length === 1 ? "" : "s"} selected
                    </Typography>

                    {/* Type breakdown chip */}
                    <Chip
                      label={`${
                        getSelectedCertificates().filter(
                          (c) => c.type === "customer"
                        ).length
                      } Customer | ${
                        getSelectedCertificates().filter(
                          (c) => c.type === "sponsorship"
                        ).length
                      } Sponsorship | ${
                        getSelectedCertificates().filter(
                          (c) => c.type === "support"
                        ).length
                        // } Multiple | ${
                        //   getSelectedCertificates().filter(
                        //     (c) => c.type === "support"
                        //   ).length
                      } Support`}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        height: { xs: 24, sm: 28 },
                      }}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {/* Bulk Status Update Button - Only show if on Active tab and has non-redeemed certificates */}
                    {activeTab === 0 &&
                      getSelectedCertificates().some(
                        (cert) => cert.current_status !== "Redeemed"
                      ) && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={
                            bulkStatusLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <FaEdit />
                            )
                          }
                          onClick={handleBulkStatusUpdate}
                          disabled={bulkStatusLoading || bulkDeleteLoading}
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            padding: { xs: "6px 12px", sm: "8px 16px" },
                            width: { xs: "100%", sm: "auto" },
                            minHeight: { xs: 36, sm: 32 },
                          }}
                        >
                          {bulkStatusLoading
                            ? "Processing..."
                            : userPermissions?.is_stock_clerk &&
                              !userPermissions?.can_approve_status
                            ? "Request Next Status"
                            : "Update to Next Status"}
                        </Button>
                      )}

                    {/* Admin Bulk Delete Button */}
                    {is_admin && getSelectedCertificates().length > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={
                          bulkDeleteLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <FaTrash />
                          )
                        }
                        onClick={handleBulkDelete}
                        disabled={bulkStatusLoading || bulkDeleteLoading}
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          padding: { xs: "6px 12px", sm: "8px 16px" },
                          width: { xs: "100%", sm: "auto" },
                          minHeight: { xs: 36, sm: 32 },
                        }}
                      >
                        {bulkDeleteLoading
                          ? "Deleting..."
                          : `Delete (${getSelectedCertificates().length})`}
                      </Button>
                    )}

                    {/* Clear Selection Button */}
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedGCs([])}
                      disabled={bulkStatusLoading || bulkDeleteLoading}
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        padding: { xs: "6px 12px", sm: "8px 16px" },
                        width: { xs: "100%", sm: "auto" },
                        minHeight: { xs: 36, sm: 32 },
                      }}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                </Box>
              </Toolbar>
            </Card>
          )}

          {/* UPDATED: Table with sorting and fixed columns */}
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate()}
                        checked={isAllSelected()}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={paginatedCertificates.length === 0}
                      />
                    </TableCell>
                    {columnOptions
                      .filter((c) => c.selected)
                      .map((column) => {
                        // Determine if this column is sortable
                        const sortableColumns = [
                          "serial_number",
                          "store_company",
                          "status",
                          "redemption_date",
                          "expiry",
                          "created",
                        ];
                        const isSortable = sortableColumns.includes(column.key);

                        // Map column keys to sort keys
                        const sortKeyMap: Record<string, string> = {
                          expiry: "expiry_date",
                          created: "created_at",
                        };
                        const sortKey = sortKeyMap[column.key] || column.key;

                        return (
                          <TableCell key={column.key}>
                            {isSortable ? (
                              <div
                                className="flex items-center space-x-1 cursor-pointer hover:bg-gray-50 p-1 rounded select-none"
                                onClick={() => handleSort(sortKey)}
                                style={{ userSelect: "none" }}
                              >
                                <span>{column.label}</span>
                                {renderSortIcon(sortKey)}
                              </div>
                            ) : (
                              <span>{column.label}</span>
                            )}
                          </TableCell>
                        );
                      })}
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCertificates.map((certificate) => {
                    const isSelected = selectedGCs.includes(certificate.id);

                    return (
                      <TableRow
                        key={certificate.id}
                        hover
                        selected={isSelected}
                        className={isSelected ? "bg-blue-50" : ""}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) =>
                              handleSelectGC(certificate.id, e.target.checked)
                            }
                            disabled={
                              isExpired(certificate.expiry_date) ||
                              isApprovalStatus(certificate.current_status) ||
                              (!canSeeCreatedGCs &&
                                certificate.current_status === "Created")
                            }
                          />
                        </TableCell>
                        {columnOptions
                          .filter((c) => c.selected)
                          .map((column) => (
                            <TableCell key={column.key}>
                              {renderColumnValue(certificate, column.key)}
                            </TableCell>
                          ))}
                        <TableCell>
                          {/* Your existing action buttons */}
                          <div className="flex space-x-1">
                            {(() => {
                              if (!hasEditPermission(certificate)) {
                                return (
                                  <Tooltip title="No Permission or Invalid Status">
                                    <span>
                                      <Button
                                        size="small"
                                        disabled
                                        variant="outlined"
                                        sx={{
                                          minWidth: "auto",
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: 3,
                                          fontSize: "0.75rem",
                                          textTransform: "none",
                                          color: "text.disabled",
                                          borderColor: "rgba(0, 0, 0, 0.12)",
                                        }}
                                      >
                                        Locked
                                      </Button>
                                    </span>
                                  </Tooltip>
                                );
                              }

                              const nextStatus = getNextStatus(
                                certificate.current_status
                              );
                              const needsApproval = requiresApproval(
                                certificate.current_status,
                                nextStatus || ""
                              );

                              return (
                                <Tooltip
                                  title={
                                    needsApproval &&
                                    userPermissions?.is_stock_clerk
                                      ? `Request Change to ${nextStatus}`
                                      : `Change to ${nextStatus}`
                                  }
                                >
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleEditStatus(certificate)
                                    }
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                      minWidth: "auto",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 3,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      textTransform: "none",
                                      boxShadow: 1,
                                      "&:hover": {
                                        boxShadow: 2,
                                      },
                                    }}
                                  >
                                    Update
                                  </Button>
                                </Tooltip>
                              );
                            })()}

                            <Tooltip title="View History">
                              <Button
                                size="small"
                                onClick={() => handleViewHistory(certificate)}
                                variant="contained"
                                color="secondary"
                                sx={{
                                  minWidth: "auto",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 3,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  boxShadow: 1,
                                  "&:hover": {
                                    boxShadow: 2,
                                  },
                                }}
                              >
                                History
                              </Button>
                            </Tooltip>

                            {is_admin && (
                              <Tooltip title="Delete Certificate">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(certificate)}
                                  variant="contained"
                                  sx={{
                                    minWidth: "auto",
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: 1,
                                    "&:hover": {
                                      boxShadow: 2,
                                    },
                                  }}
                                >
                                  Delete
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredCertificates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Card>
        </>
      )}

      {/* Status Confirmation Dialog */}
      <Dialog
        open={statusConfirmDialog}
        onClose={() => setStatusConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaEdit className="text-blue-500" />
            <span>
              {pendingStatusChange
                ? `Confirm Change Status to ${
                    STATUS_LABELS[
                      pendingStatusChange.newStatus as keyof typeof STATUS_LABELS
                    ] || pendingStatusChange.newStatus
                  }`
                : "Confirm Status Change"}
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            {pendingStatusChange && (
              <>
                <Alert severity="info" className="mb-4">
                  <Typography variant="body2">
                    You are about to change the status of this gift certificate.
                  </Typography>
                </Alert>

                <Card className="bg-gray-50">
                  <CardContent>
                    <Typography variant="h6" className="mb-3">
                      Certificate Details
                    </Typography>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Serial Number:</span>
                        <span className="font-mono font-semibold">
                          #{pendingStatusChange.gc.serial_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type:</span>
                        <Chip
                          label={pendingStatusChange.gc.type}
                          size="small"
                          color={
                            pendingStatusChange.gc.type === "customer"
                              ? "success"
                              : pendingStatusChange.gc.type === "sponsorship"
                              ? "secondary"
                              : "info"
                          }
                        />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Status:</span>
                        <Chip
                          label={
                            STATUS_LABELS[
                              pendingStatusChange.gc
                                .current_status as keyof typeof STATUS_LABELS
                            ] || pendingStatusChange.gc.current_status
                          }
                          size="small"
                          color={
                            getStatusColor(
                              pendingStatusChange.gc.current_status
                            ) as any
                          }
                        />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">New Status:</span>
                        <Chip
                          label={
                            STATUS_LABELS[
                              pendingStatusChange.newStatus as keyof typeof STATUS_LABELS
                            ] || pendingStatusChange.newStatus
                          }
                          size="small"
                          color={
                            getStatusColor(pendingStatusChange.newStatus) as any
                          }
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any notes about this status change..."
                  className="mt-4"
                />

                {/* Store Selection and Redemption Date for Redeemed status */}
                {showStoreSelection &&
                  pendingStatusChange?.newStatus === "Redeemed" && (
                    <>
                      <div className="mt-4">
                        <FormControl fullWidth>
                          <InputLabel>Redeemed At Store *</InputLabel>
                          <Select
                            value={redeemedAtStore || ""}
                            onChange={(e) =>
                              setRedeemedAtStore(Number(e.target.value))
                            }
                            label="Redeemed At Store *"
                            required
                          >
                            {giftCertificateState.userStores?.map((store) => (
                              <MenuItem key={store.id} value={store.id}>
                                {store.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {/* Redemption Date Picker */}
                      <div className="mt-4">
                        <TextField
                          fullWidth
                          type="date"
                          label="Redemption Date"
                          value={redemptionDate}
                          onChange={(e) => setRedemptionDate(e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            max: new Date().toISOString().split("T")[0], // Cannot exceed current date
                          }}
                          required
                          helperText="Select the date when this certificate was redeemed"
                        />
                      </div>
                    </>
                  )}
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmStatusChange}
            variant="contained"
            color="primary"
            startIcon={<FaEdit />}
            disabled={
              showStoreSelection && (!redeemedAtStore || !redemptionDate)
            }
          >
            Confirm Status Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Request Dialog */}
      <Dialog
        open={statusRequestDialog}
        onClose={() => setStatusRequestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaClock className="text-orange-500" />
            <span>
              {pendingStatusChange
                ? `Request Change Status to ${
                    STATUS_LABELS[
                      pendingStatusChange.newStatus as keyof typeof STATUS_LABELS
                    ] || pendingStatusChange.newStatus
                  }`
                : "Request Status Change"}
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            {pendingStatusChange && (
              <>
                <Alert severity="info" className="mb-4">
                  <Typography variant="body2">
                    You are requesting a status change for this gift
                    certificate. It will need approval from a Team Captain,
                    Manager, or Administrator.
                  </Typography>
                </Alert>

                <Card className="bg-gray-50">
                  <CardContent>
                    <Typography variant="h6" className="mb-3">
                      Certificate Details
                    </Typography>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Serial Number:</span>
                        <span className="font-mono font-semibold">
                          #{pendingStatusChange.gc.serial_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Status:</span>
                        <Chip
                          label={
                            STATUS_LABELS[
                              pendingStatusChange.gc
                                .current_status as keyof typeof STATUS_LABELS
                            ] || pendingStatusChange.gc.current_status
                          }
                          size="small"
                          color={
                            getStatusColor(
                              pendingStatusChange.gc.current_status
                            ) as any
                          }
                        />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Requested Status:</span>
                        <Chip
                          label={
                            STATUS_LABELS[
                              pendingStatusChange.newStatus as keyof typeof STATUS_LABELS
                            ] || pendingStatusChange.newStatus
                          }
                          size="small"
                          color={
                            getStatusColor(pendingStatusChange.newStatus) as any
                          }
                          variant="outlined"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Request Notes (Optional)"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Add any notes about why you're requesting this status change..."
                  className="mt-4"
                />

                {showStoreSelection &&
                  pendingStatusChange?.newStatus === "Redeemed" && (
                    <>
                      <div className="mt-4">
                        <FormControl fullWidth>
                          <InputLabel>Redeemed At Store *</InputLabel>
                          <Select
                            value={redeemedAtStore || ""}
                            onChange={(e) =>
                              setRedeemedAtStore(Number(e.target.value))
                            }
                            label="Redeemed At Store *"
                            required
                          >
                            {giftCertificateState.userStores?.map((store) => (
                              <MenuItem key={store.id} value={store.id}>
                                {store.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {/* Redemption Date Picker */}
                      <div className="mt-4">
                        <TextField
                          fullWidth
                          type="date"
                          label="Redemption Date"
                          value={redemptionDate}
                          onChange={(e) => setRedemptionDate(e.target.value)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            max: new Date().toISOString().split("T")[0], // Cannot exceed current date
                          }}
                          required
                          helperText="Select the date when this certificate was redeemed"
                        />
                      </div>
                    </>
                  )}
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusRequestDialog(false)}>Cancel</Button>
          <Button
            onClick={submitStatusRequest}
            variant="contained"
            color="primary"
            startIcon={<FaClock />}
            disabled={showStoreSelection && !redeemedAtStore}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* UPDATED: Review Dialog - Dynamic title */}
      <Dialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            {reviewAction === "approved" ? (
              <FaCheck className="text-green-500" />
            ) : (
              <FaTimes className="text-red-500" />
            )}
            <span>
              {reviewAction === "approved" ? "Approve" : "Reject"} Change to{" "}
              {selectedRequest
                ? STATUS_LABELS[
                    selectedRequest.requested_status as keyof typeof STATUS_LABELS
                  ] || selectedRequest.requested_status
                : "Status"}
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            {selectedRequest && (
              <>
                <Alert
                  severity={reviewAction === "approved" ? "success" : "warning"}
                  className="mb-4"
                >
                  <Typography variant="body2">
                    You are about to{" "}
                    <strong>
                      {reviewAction === "approved" ? "approve" : "reject"}
                    </strong>{" "}
                    this status change request.
                    {reviewAction === "approved" &&
                      " The gift certificate status will be updated immediately."}
                  </Typography>
                </Alert>

                <Card className="bg-gray-50 mb-4">
                  <CardContent>
                    <Typography variant="h6" className="mb-3">
                      Request Details
                    </Typography>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Certificate
                        </Typography>
                        <Typography
                          variant="body2"
                          className="font-mono font-semibold"
                        >
                          #{selectedRequest.serial_number}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Type
                        </Typography>
                        <Chip
                          label={selectedRequest.type}
                          size="small"
                          color={
                            selectedRequest.type === "customer"
                              ? "success"
                              : selectedRequest.type === "sponsorship"
                              ? "secondary"
                              : // : selectedRequest.type === "multiple"
                              // ? "info"
                              selectedRequest.type === "support"
                              ? "warning"
                              : "default"
                          }
                        />
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Current Status
                        </Typography>
                        <Chip
                          label={
                            STATUS_LABELS[
                              selectedRequest.current_status as keyof typeof STATUS_LABELS
                            ] || selectedRequest.current_status
                          }
                          size="small"
                          color={
                            getStatusColor(
                              selectedRequest.current_status
                            ) as any
                          }
                        />
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Requested Status
                        </Typography>
                        <Chip
                          label={
                            STATUS_LABELS[
                              selectedRequest.requested_status as keyof typeof STATUS_LABELS
                            ] || selectedRequest.requested_status
                          }
                          size="small"
                          color={
                            getStatusColor(
                              selectedRequest.requested_status
                            ) as any
                          }
                          variant="outlined"
                        />
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Requested By
                        </Typography>
                        <Typography variant="body2">
                          {selectedRequest.requester_first_name}{" "}
                          {selectedRequest.requester_last_name}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="textSecondary">
                          Request Date
                        </Typography>
                        <Typography variant="body2">
                          {new Date(
                            selectedRequest.created_at
                          ).toLocaleDateString()}
                        </Typography>
                      </div>
                    </div>

                    {selectedRequest.request_notes && (
                      <div className="mt-4">
                        <Typography variant="body2" color="textSecondary">
                          Request Notes
                        </Typography>
                        <Typography
                          variant="body2"
                          className="bg-white p-2 rounded border"
                        >
                          {selectedRequest.request_notes}
                        </Typography>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Review Notes (Optional)"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={`Add notes about why you ${reviewAction} this request...`}
                />
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
          <Button
            onClick={submitReview}
            variant="contained"
            color={reviewAction === "approved" ? "success" : "error"}
            startIcon={reviewAction === "approved" ? <FaCheck /> : <FaTimes />}
          >
            {reviewAction === "approved"
              ? `Confirm Status Change to "${
                  selectedRequest
                    ? (
                        STATUS_LABELS[
                          selectedRequest.requested_status as keyof typeof STATUS_LABELS
                        ] || selectedRequest.requested_status
                      ).split(" - ")[0]
                    : "Status"
                }"`
              : "Reject Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD: Bulk Approval Dialog */}
      <Dialog
        open={bulkApprovalDialogOpen}
        onClose={() => setBulkApprovalDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaCheck className="text-green-500" />
            <span>Bulk Approve Status Change Requests</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="success" className="mb-4">
              <Typography variant="body2">
                You are about to <strong>approve</strong>{" "}
                {selectedApprovalRequests.length} status change request
                {selectedApprovalRequests.length > 1 ? "s" : ""}. The gift
                certificate statuses will be updated immediately.
              </Typography>
            </Alert>

            <Card className="mb-4 bg-green-50 border border-green-200">
              <CardContent>
                <Typography variant="h6" className="mb-2 text-green-800">
                  Requests to Approve ({selectedApprovalRequests.length})
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {pendingRequests
                    .filter((req) => selectedApprovalRequests.includes(req.id))
                    .map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between text-sm p-2 bg-white rounded border"
                      >
                        <div>
                          <span className="font-mono font-semibold">
                            #{request.serial_number}
                          </span>
                          <div className="text-xs text-gray-500">
                            {request.current_status} →{" "}
                            {request.requested_status}
                          </div>
                        </div>
                        <Chip
                          label={request.type}
                          size="small"
                          color={
                            request.type === "customer"
                              ? "success"
                              : request.type === "sponsorship"
                              ? "secondary"
                              : // : request.type === "multiple"
                              // ? "info"
                              request.type === "support"
                              ? "warning"
                              : "default"
                          }
                        />
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Review Notes (Optional)"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add notes about this bulk approval..."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkApprovalDialogOpen(false)}
            disabled={bulkApprovalLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitBulkApproval}
            variant="contained"
            color="success"
            disabled={
              selectedApprovalRequests.length === 0 || bulkApprovalLoading
            }
            startIcon={
              bulkApprovalLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FaCheck />
              )
            }
          >
            {bulkApprovalLoading
              ? "Approving..."
              : `Confirm ${selectedApprovalRequests.length} Status Change${
                  selectedApprovalRequests.length > 1 ? "s" : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Bulk Decline Dialog */}
      <Dialog
        open={bulkDeclineDialogOpen}
        onClose={() => setBulkDeclineDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaTimes className="text-red-500" />
            <span>Bulk Decline Status Change Requests</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="warning" className="mb-4">
              <Typography variant="body2">
                You are about to <strong>decline</strong>{" "}
                {selectedApprovalRequests.length} status change request
                {selectedApprovalRequests.length > 1 ? "s" : ""}. The gift
                certificates will be reverted to their original status.
              </Typography>
            </Alert>

            <Card className="mb-4 bg-red-50 border border-red-200">
              <CardContent>
                <Typography variant="h6" className="mb-2 text-red-800">
                  Requests to Decline ({selectedApprovalRequests.length})
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {pendingRequests
                    .filter((req: StatusChangeRequest) => {
                      // Apply the same filtering logic as renderPendingApprovalsTab
                      if (
                        !canSeeRedeemApprovals &&
                        req.requested_status === "Redeemed"
                      ) {
                        return false;
                      }
                      return selectedApprovalRequests.includes(req.id);
                    })
                    .map((request: StatusChangeRequest) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between text-sm p-2 bg-white rounded border"
                      >
                        <div>
                          <span className="font-mono font-semibold">
                            #{request.serial_number}
                          </span>
                          <div className="text-xs text-gray-500">
                            {request.current_status} →{" "}
                            {request.requested_status}
                          </div>
                        </div>
                        <Chip
                          label={request.type}
                          size="small"
                          color={
                            request.type === "customer"
                              ? "success"
                              : request.type === "sponsorship"
                              ? "secondary"
                              : // : request.type === "multiple"
                              // ? "info"
                              request.type === "support"
                              ? "warning"
                              : "default"
                          }
                        />
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Decline Notes (Optional)"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add notes about why you're declining these requests..."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeclineDialogOpen(false)}
            disabled={bulkDeclineLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitBulkDecline}
            variant="contained"
            color="error"
            disabled={
              selectedApprovalRequests.length === 0 || bulkDeclineLoading
            }
            startIcon={
              bulkDeclineLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FaTimes />
              )
            }
          >
            {bulkDeclineLoading
              ? "Declining..."
              : `Decline ${selectedApprovalRequests.length} Request${
                  selectedApprovalRequests.length > 1 ? "s" : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* UPDATED: Bulk Status Update Dialog - Sequential */}
      <Dialog
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaEdit className="text-blue-500" />
            <span>
              {userPermissions?.is_stock_clerk &&
              !userPermissions?.can_approve_status
                ? "Bulk Request Next Status"
                : "Bulk Update to Next Status"}
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert
              severity={
                userPermissions?.is_stock_clerk &&
                !userPermissions?.can_approve_status
                  ? "info"
                  : "warning"
              }
              className="mb-4"
            >
              <Typography variant="body2">
                {userPermissions?.is_stock_clerk &&
                !userPermissions?.can_approve_status
                  ? `You are about to request the next status for ${
                      getSelectedCertificates().length
                    } gift certificates. Each will require approval.`
                  : `You are about to update ${
                      getSelectedCertificates().length
                    } gift certificates to their next status in sequence.`}
              </Typography>
            </Alert>

            <Card className="mb-4 bg-gray-50">
              <CardContent>
                <Typography variant="h6" className="mb-2">
                  Selected Certificates ({getSelectedCertificates().length})
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {getSelectedCertificates()
                    .filter((cert) => cert.current_status !== "Redeemed")
                    .slice(0, 10)
                    .map((cert) => {
                      const nextStatus = getNextStatus(cert.current_status);
                      return (
                        <div
                          key={cert.id}
                          className="flex items-center justify-between text-sm p-2 bg-white rounded"
                        >
                          <div>
                            <span className="font-mono">
                              #{cert.serial_number}
                            </span>
                            <div className="text-xs text-gray-500">
                              {STATUS_LABELS[
                                cert.current_status as keyof typeof STATUS_LABELS
                              ] || cert.current_status}{" "}
                              →{" "}
                              {nextStatus
                                ? STATUS_LABELS[
                                    nextStatus as keyof typeof STATUS_LABELS
                                  ] || nextStatus
                                : "No next status"}
                            </div>
                          </div>
                          <Chip
                            label={cert.type}
                            size="small"
                            color={
                              cert.type === "customer"
                                ? "success"
                                : cert.type === "sponsorship"
                                ? "secondary"
                                : "info"
                            }
                          />
                        </div>
                      );
                    })}
                  {getSelectedCertificates().filter(
                    (cert) => cert.current_status !== "Redeemed"
                  ).length > 10 && (
                    <div className="text-center text-gray-500 text-sm p-2">
                      +
                      {getSelectedCertificates().filter(
                        (cert) => cert.current_status !== "Redeemed"
                      ).length - 10}{" "}
                      more certificates...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              multiline
              rows={3}
              label={
                userPermissions?.is_stock_clerk &&
                !userPermissions?.can_approve_status
                  ? "Request Notes (Optional)"
                  : "Notes (Optional)"
              }
              value={bulkStatusNotes}
              onChange={(e) => setBulkStatusNotes(e.target.value)}
              placeholder={
                userPermissions?.is_stock_clerk &&
                !userPermissions?.can_approve_status
                  ? "Add notes about these status change requests..."
                  : "Add notes about this bulk status update..."
              }
              className="mb-4"
            />

            {/* Store selection and Redemption Date for bulk redeemed certificates */}
            {showBulkStoreSelection && (
              <>
                <div className="mt-4">
                  <Alert severity="info" className="mb-3">
                    <Typography variant="body2">
                      Some certificates will be marked as "Redeemed". Please
                      select the store where they will be redeemed and the
                      redemption date.
                    </Typography>
                  </Alert>
                  <FormControl fullWidth>
                    <InputLabel>Redeemed At Store *</InputLabel>
                    <Select
                      value={bulkRedeemedAtStore || ""}
                      onChange={(e) =>
                        setBulkRedeemedAtStore(Number(e.target.value))
                      }
                      label="Redeemed At Store *"
                      required
                    >
                      {giftCertificateState.userStores?.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* NEW: Bulk Redemption Date Picker */}
                <div className="mt-4">
                  <TextField
                    fullWidth
                    type="date"
                    label="Redemption Date"
                    value={bulkRedemptionDate}
                    onChange={(e) => setBulkRedemptionDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0], // Cannot exceed current date
                    }}
                    required
                    helperText="Select the date when these certificates were redeemed"
                  />
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkStatusDialogOpen(false);
              setBulkRedeemedAtStore(null);
              <Button
                onClick={() => {
                  setBulkStatusDialogOpen(false);
                  setBulkRedeemedAtStore(null);
                  setBulkRedemptionDate(""); // ADDED
                  setShowBulkStoreSelection(false);
                }}
                disabled={bulkStatusLoading}
              >
                Cancel
              </Button>;
              setShowBulkStoreSelection(false);
            }}
            disabled={bulkStatusLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitBulkStatusUpdate}
            variant="contained"
            disabled={
              getSelectedCertificates().filter(
                (cert) => cert.current_status !== "Redeemed"
              ).length === 0 ||
              bulkStatusLoading ||
              (showBulkStoreSelection &&
                (!bulkRedeemedAtStore || !bulkRedemptionDate)) // Disable if store selection required but not selected
            }
            startIcon={
              bulkStatusLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FaEdit />
              )
            }
          >
            {bulkStatusLoading
              ? "Processing..."
              : userPermissions?.is_stock_clerk &&
                !userPermissions?.can_approve_status
              ? `Request Next Status (${
                  getSelectedCertificates().filter(
                    (cert) => cert.current_status !== "Redeemed"
                  ).length
                })`
              : `Update to Next Status (${
                  getSelectedCertificates().filter(
                    (cert) => cert.current_status !== "Redeemed"
                  ).length
                })`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FIXED: Bulk Delete Dialog with proper multiple type handling */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaTrash className="text-red-500" />
            <span>Bulk Delete Gift Certificates (Admin Only)</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="warning" className="mb-4">
              <Typography variant="body2">
                <strong>Administrator Action:</strong> This action cannot be
                undone. You are about to delete{" "}
                <strong>{getSelectedCertificates().length}</strong> gift
                certificates.
              </Typography>
            </Alert>

            <Card className="mb-4 bg-red-50 border border-red-200">
              <CardContent>
                <Typography variant="h6" className="mb-2 text-red-800">
                  Certificates to be Deleted ({getSelectedCertificates().length}
                  )
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {getSelectedCertificates().map((cert) => (
                    <div
                      key={cert.id}
                      className="flex items-center justify-between text-sm p-2 bg-white rounded border"
                    >
                      <div>
                        <span className="font-mono font-semibold">
                          #{cert.serial_number}
                        </span>
                        <div className="text-xs text-gray-500">
                          {cert.type === "support"
                            ? cert.store_name
                            : cert.type === "customer"
                            ? cert.group_name
                            : cert.company_name}
                        </div>
                      </div>
                      <Chip
                        label={
                          STATUS_LABELS[
                            cert.current_status as keyof typeof STATUS_LABELS
                          ] || cert.current_status
                        }
                        size="small"
                        color={getStatusColor(cert.current_status) as any}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            disabled={bulkDeleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitBulkDelete}
            variant="contained"
            color="error"
            disabled={
              getSelectedCertificates().length === 0 || bulkDeleteLoading
            }
            startIcon={
              bulkDeleteLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FaTrash />
              )
            }
          >
            {bulkDeleteLoading
              ? "Deleting..."
              : `Delete ${getSelectedCertificates().length} Certificates`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Selection Popover */}
      <Popover
        open={Boolean(columnAnchorEl)}
        anchorEl={columnAnchorEl}
        onClose={() => setColumnAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Select Columns to Display
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 350, overflow: "auto" }}>
          {/* Mandatory Columns */}
          <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Typography variant="subtitle2" gutterBottom>
              Mandatory Columns
            </Typography>
            {columnOptions
              .filter((option) => option.mandatory)
              .map((option) => (
                <FormControlLabel
                  key={option.key}
                  control={<Checkbox checked={true} disabled size="small" />}
                  label={
                    <Typography variant="body2">{option.label}</Typography>
                  }
                />
              ))}
          </Box>
          {/* Optional Columns */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Optional Columns
            </Typography>
            {columnOptions
              .filter((option) => !option.mandatory)
              .map((option, index) => (
                <FormControlLabel
                  key={option.key}
                  control={
                    <Checkbox
                      checked={option.selected}
                      onChange={() => {
                        const updatedOptions = [...columnOptions];
                        const optionIndex = updatedOptions.findIndex(
                          (c) => c.key === option.key
                        );
                        updatedOptions[optionIndex].selected =
                          !updatedOptions[optionIndex].selected;
                        setColumnOptions(updatedOptions);
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">{option.label}</Typography>
                  }
                />
              ))}
          </Box>
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              const resetColumns = columnOptions.map((col) => ({
                ...col,
                selected: col.mandatory
                  ? true
                  : col.key === "expiry" || col.key === "created",
              }));
              setColumnOptions(resetColumns);
            }}
            color="error"
            size="small"
          >
            Reset to Default
          </Button>
          <Button
            onClick={() => setColumnAnchorEl(null)}
            variant="contained"
            size="small"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Status - #{selectedGC?.serial_number}</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="New Status"
              >
                {validNextStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {STATUS_LABELS[status as keyof typeof STATUS_LABELS] ||
                      status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
              placeholder="Add any notes about this status change..."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitStatusUpdate}
            variant="contained"
            disabled={
              !newStatus ||
              giftCertificateState.status === GiftCertificateState.inProgress
            }
            startIcon={
              giftCertificateState.status ===
              GiftCertificateState.inProgress ? (
                <CircularProgress size={20} />
              ) : undefined
            }
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* FIXED: History Dialog with proper multiple type handling */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          dispatch(clearCurrentCertificate());
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Status History - #{currentCertificate?.serial_number}
        </DialogTitle>
        <DialogContent>
          {currentCertificate && (
            <div className="space-y-4 pt-2">
              <Card className="bg-gray-50">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Type
                      </Typography>
                      <Chip
                        label={currentCertificate.type}
                        size="small"
                        color={
                          currentCertificate.type === "customer"
                            ? "success"
                            : currentCertificate.type === "sponsorship"
                            ? "secondary"
                            : // : currentCertificate.type === "multiple"
                            // ? "info"
                            currentCertificate.type === "support"
                            ? "warning"
                            : "default"
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Current Status
                      </Typography>
                      <Chip
                        label={
                          STATUS_LABELS[
                            currentCertificate.current_status as keyof typeof STATUS_LABELS
                          ] || currentCertificate.current_status
                        }
                        size="small"
                        color={
                          getStatusColor(
                            currentCertificate.current_status
                          ) as any
                        }
                      />
                    </Grid>
                    {currentCertificate.store_name &&
                      currentCertificate.type === "support" && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Store
                          </Typography>
                          <Typography variant="body2">
                            {currentCertificate.store_name}
                          </Typography>
                        </Grid>
                      )}
                    {currentCertificate.group_name &&
                      currentCertificate.type !== "support" && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">
                            Group
                          </Typography>
                          <Typography variant="body2">
                            {currentCertificate.group_name}
                          </Typography>
                        </Grid>
                      )}
                    {currentCertificate.company_name && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Company
                        </Typography>
                        <Typography variant="body2">
                          {currentCertificate.company_name}
                        </Typography>
                      </Grid>
                    )}
                    {currentCertificate.expiry_date && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Expiry Date
                        </Typography>
                        <Typography
                          variant="body2"
                          className={
                            isExpired(currentCertificate.expiry_date)
                              ? "text-red-600 font-semibold"
                              : ""
                          }
                        >
                          {new Date(
                            currentCertificate.expiry_date
                          ).toLocaleDateString()}
                          {isExpired(currentCertificate.expiry_date) &&
                            " (Expired)"}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* UPDATED: Enhanced Attachments Section - Only show for admins */}
              {is_admin && giftCertificateState.gcAttachments?.length > 0 && (
                <div className="mb-6">
                  <Typography variant="h6" className="mb-3">
                    Attachments ({giftCertificateState.gcAttachments.length})
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {giftCertificateState.gcAttachments.map((attachment) => {
                      const isImage = [
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                      ].includes(attachment.file_type);

                      return (
                        <Card
                          key={attachment.id}
                          variant="outlined"
                          className="overflow-hidden"
                        >
                          <CardContent className="p-0">
                            {/* Image Preview Section */}
                            {isImage ? (
                              <div className="relative">
                                <div
                                  className="aspect-video bg-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                                  onClick={() => handleImageClick(attachment)}
                                >
                                  <img
                                    src={`${process.env.REACT_APP_DOMAIN_URL}api/gift-certificate/serve-image/${attachment.id}`}
                                    alt={attachment.file_name}
                                    className="w-full h-full object-contain hover:scale-105 transition-transform"
                                    onError={handleImageError}
                                    loading="lazy"
                                  />
                                </div>
                                <div className="absolute top-2 right-2">
                                  <Chip
                                    label="Click to enlarge"
                                    size="small"
                                    className="bg-black bg-opacity-50 text-white text-xs"
                                  />
                                </div>
                              </div>
                            ) : (
                              // PDF/Other file preview
                              <div className="aspect-video bg-gray-100 flex flex-col items-center justify-center">
                                <div className="text-4xl mb-2">📄</div>
                                <Typography
                                  variant="body2"
                                  className="text-gray-600 text-center"
                                >
                                  {attachment.file_type
                                    .split("/")[1]
                                    .toUpperCase()}{" "}
                                  File
                                </Typography>
                              </div>
                            )}

                            {/* File Info Section */}
                            <div className="p-3">
                              <Typography
                                variant="body2"
                                className="font-medium truncate mb-2"
                                title={attachment.file_name}
                              >
                                {attachment.file_name}
                              </Typography>

                              <div className="flex items-center justify-between mb-3">
                                <div className="text-xs text-gray-500">
                                  <div>
                                    {(
                                      attachment.file_size /
                                      1024 /
                                      1024
                                    ).toFixed(2)}{" "}
                                    MB
                                  </div>
                                  <div>
                                    {attachment.file_type
                                      .split("/")[1]
                                      .toUpperCase()}
                                  </div>
                                </div>
                                <Chip
                                  label={attachment.file_type
                                    .split("/")[1]
                                    .toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  color={isImage ? "success" : "primary"}
                                />
                              </div>

                              <div className="text-xs text-gray-500 mb-3">
                                <div>
                                  Uploaded by {attachment.first_name}{" "}
                                  {attachment.last_name}
                                </div>
                                <div>
                                  {new Date(
                                    attachment.uploaded_at
                                  ).toLocaleDateString()}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                {isImage && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleImageClick(attachment)}
                                    className="flex-1"
                                  >
                                    View
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    window.open(
                                      `${process.env.REACT_APP_DOMAIN_URL}api/gift-certificate/download-attachment/${attachment.id}`,
                                      "_blank"
                                    );
                                  }}
                                  className={isImage ? "flex-1" : "w-full"}
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ADD: Image Modal Dialog */}
              <Dialog
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                  style: {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    overflow: "visible",
                  },
                }}
              >
                <DialogContent
                  style={{
                    padding: 0,
                    backgroundColor: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {selectedImage && (
                    <>
                      <div className="relative max-w-full max-h-screen">
                        <img
                          src={selectedImage.src}
                          alt={selectedImage.name}
                          className="max-w-full max-h-screen object-contain rounded-lg shadow-2xl"
                        />
                        <IconButton
                          onClick={() => setImageModalOpen(false)}
                          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white hover:bg-opacity-75"
                          size="small"
                        >
                          <FaTimes />
                        </IconButton>
                      </div>
                      <Typography
                        variant="body2"
                        className="mt-2 text-white bg-black bg-opacity-50 px-3 py-1 rounded max-w-full truncate"
                      >
                        {selectedImage.name}
                      </Typography>
                    </>
                  )}
                </DialogContent>
              </Dialog>

              <div>
                <Typography variant="h6" className="mb-3">
                  Status History
                </Typography>
                <div className="space-y-3">
                  {statusHistory.map((history) => (
                    <Card
                      key={history.id}
                      className="border-l-4 border-blue-400"
                    >
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Chip
                              label={
                                STATUS_LABELS[
                                  history.status as keyof typeof STATUS_LABELS
                                ] || history.status
                              }
                              size="small"
                              color={getStatusColor(history.status) as any}
                              icon={getStatusIcon(history.status)}
                            />
                            <Typography variant="body2" color="textSecondary">
                              by {history.first_name} {history.last_name}
                            </Typography>
                          </div>
                          <Typography variant="body2" color="textSecondary">
                            {new Date(history.changed_at).toLocaleDateString()}{" "}
                            {new Date(history.changed_at).toLocaleTimeString()}
                          </Typography>
                        </div>
                        {history.notes && (
                          <Typography
                            variant="body2"
                            className="text-gray-700 bg-gray-50 p-2 rounded"
                          >
                            {history.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setHistoryDialogOpen(false);
              dispatch(clearCurrentCertificate());
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* FIXED: Delete Confirmation Dialog with proper multiple type handling */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaTrash className="text-red-500" />
            <span>Delete Gift Certificate</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-2">
            <Alert severity="warning" className="mb-4">
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone. The gift
                certificate will be permanently deleted.
              </Typography>
            </Alert>

            {gcToDelete && (
              <Card className="bg-gray-50">
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Certificate Details
                  </Typography>
                  <div className="space-y-2">
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Serial Number
                      </Typography>
                      <Typography
                        variant="body2"
                        className="font-semibold font-mono"
                      >
                        #{gcToDelete.serial_number}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Type
                      </Typography>
                      <Chip
                        label={gcToDelete.type}
                        size="small"
                        color={
                          gcToDelete.type === "customer"
                            ? "success"
                            : gcToDelete.type === "sponsorship"
                            ? "secondary"
                            : "info"
                        }
                      />
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Status
                      </Typography>
                      <Chip
                        label={
                          STATUS_LABELS[
                            gcToDelete.current_status as keyof typeof STATUS_LABELS
                          ] || gcToDelete.current_status
                        }
                        size="small"
                        color={getStatusColor(gcToDelete.current_status) as any}
                      />
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        {gcToDelete.type === "support"
                          ? "Store"
                          : gcToDelete.type === "customer"
                          ? "Group"
                          : "Company"}
                      </Typography>
                      <Typography variant="body2">
                        {gcToDelete.type === "support"
                          ? gcToDelete.store_name
                          : gcToDelete.type === "customer"
                          ? gcToDelete.group_name
                          : gcToDelete.company_name}
                      </Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<FaTrash />}
          >
            Delete Certificate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => !isExporting && setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center space-x-2">
            <FaDownload className="text-green-500" />
            <span>Export Redeemed Gift Certificates to Excel</span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="pt-4 space-y-4">
            {/* Export Option */}
            <div>
              <Typography variant="subtitle2" className="mb-2 font-semibold">
                Export Format
              </Typography>
              <FormControl component="fieldset">
                <div className="space-y-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOption === "all-columns"}
                        onChange={() => setExportOption("all-columns")}
                      />
                    }
                    label={
                      <div>
                        <Typography variant="body2" className="font-medium">
                          All Selected Columns
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Export with all columns currently visible in the table
                          ({columnOptions.filter((c) => c.selected).length}{" "}
                          columns)
                        </Typography>
                      </div>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOption === "with-history"}
                        onChange={() => setExportOption("with-history")}
                      />
                    }
                    label={
                      <div>
                        <Typography variant="body2" className="font-medium">
                          Default Columns + Status History
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Two sheets: Basic info + Complete status history for
                          each GC
                        </Typography>
                      </div>
                    }
                  />
                </div>
              </FormControl>
            </div>

            {/* Export Scope */}
            <div>
              <Typography variant="subtitle2" className="mb-2 font-semibold">
                Export Scope
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={exportScope}
                  onChange={(e) => setExportScope(e.target.value as any)}
                >
                  <MenuItem value="all">
                    All Redeemed GCs ({filteredCertificates.length}{" "}
                    certificates)
                  </MenuItem>
                  <MenuItem value="current-page">
                    Current Page Only ({paginatedCertificates.length}{" "}
                    certificates)
                  </MenuItem>
                  <MenuItem value="multiple-pages">
                    Multiple Pages (Custom)
                  </MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Page Count Selection */}
            {exportScope === "multiple-pages" && (
              <div>
                <Typography variant="subtitle2" className="mb-2 font-semibold">
                  Number of Pages to Export
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  value={exportPageCount}
                  onChange={(e) =>
                    setExportPageCount(
                      Math.max(
                        1,
                        Math.min(
                          parseInt(e.target.value) || 1,
                          Math.ceil(filteredCertificates.length / rowsPerPage)
                        )
                      )
                    )
                  }
                  inputProps={{
                    min: 1,
                    max: Math.ceil(filteredCertificates.length / rowsPerPage),
                  }}
                  helperText={`Max: ${Math.ceil(
                    filteredCertificates.length / rowsPerPage
                  )} pages (${exportPageCount * rowsPerPage} certificates)`}
                />
              </div>
            )}

            {/* Summary */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Export Summary:</strong>
                <br />
                Format:{" "}
                {exportOption === "all-columns"
                  ? "All Selected Columns"
                  : "Default + History"}
                <br />
                Certificates:{" "}
                {exportScope === "all"
                  ? filteredCertificates.length
                  : exportScope === "current-page"
                  ? paginatedCertificates.length
                  : Math.min(
                      exportPageCount * rowsPerPage,
                      filteredCertificates.length
                    )}
              </Typography>
            </Alert>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExportDialogOpen(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={exportToExcel}
            variant="contained"
            color="success"
            disabled={isExporting}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FaDownload />
              )
            }
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay */}
      {(bulkStatusLoading ||
        bulkDeleteLoading ||
        bulkApprovalLoading ||
        bulkDeclineLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6">
            <CardContent className="flex items-center space-x-4">
              <CircularProgress size={40} />
              <div>
                <Typography variant="h6">
                  {bulkStatusLoading
                    ? "Processing Status Changes..."
                    : bulkApprovalLoading
                    ? "Approving Requests..."
                    : bulkDeclineLoading
                    ? "Declining Requests..."
                    : "Deleting Certificates..."}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Please wait...
                </Typography>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
